import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { useToast } from '../hooks/use-toast';
import { Send, Loader2, CheckCircle2 } from 'lucide-react';
import { applicationsApi } from '../utils/api';
import {
  getProductByLabel,
  formatAmountAz,
} from '../config/products';

// This form applies only to İstehlak Krediti — the sole product offered online.
// Other products (Qızıl / Avto Lizinq) require an in-person / phone consultation.
const APPLICATION_PRODUCT_LABEL = 'İstehlak Krediti';
const APPLICATION_PRODUCT = getProductByLabel(APPLICATION_PRODUCT_LABEL);

const validatePhone = (phone) => /^\+994\s?\d{2}\s?\d{3}\s?\d{2}\s?\d{2}$/.test(phone);

const formatAzPhone = (raw) => {
  let digits = String(raw || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('994')) {
    digits = digits.slice(0, 12);
  } else if (digits.startsWith('0')) {
    digits = ('994' + digits.slice(1)).slice(0, 12);
  } else {
    digits = ('994' + digits).slice(0, 12);
  }
  const rest = digits.slice(3);
  let out = '+994';
  if (rest.length > 0) out += ' ' + rest.slice(0, 2);
  if (rest.length > 2) out += ' ' + rest.slice(2, 5);
  if (rest.length > 5) out += ' ' + rest.slice(5, 7);
  if (rest.length > 7) out += ' ' + rest.slice(7, 9);
  return out;
};

const AMOUNT_MIN_APP = APPLICATION_PRODUCT.amountMin;
const AMOUNT_MAX_APP = APPLICATION_PRODUCT.amountMax;

const FIELD_ORDER = [
  'firstName',
  'lastName',
  'phone',
  'address',
  'loanAmount',
  'loanTerm',
  'privacyConsent',
];

/**
 * Reusable preliminary-application form. Used both as a full page and inside a modal.
 * Props:
 *   - prefillAmount?: string  (optional pre-filled amount, e.g. from calculator)
 *   - prefillTerm?: string    (optional pre-filled term in months, e.g. from calculator)
 *   - onSuccess?: () => void  (called when user closes the success view)
 *   - variant?: 'page' | 'modal' (controls surrounding chrome — headings, padding)
 */
export const ApplicationForm = ({ prefillAmount = '', prefillTerm = '', onSuccess, variant = 'page' }) => {
  const { toast } = useToast();
  const isModal = variant === 'modal';

  const initial = useMemo(() => {
    const digits = String(prefillAmount ?? '').replace(/\D/g, '');
    const n = Number(digits);
    const withinRange =
      digits && !isNaN(n) && n >= AMOUNT_MIN_APP && n <= AMOUNT_MAX_APP;
    // Accept a prefilled term if it falls within İstehlak Krediti's allowed range.
    const termDigits = String(prefillTerm ?? '').replace(/\D/g, '');
    const termNum = Number(termDigits);
    const termInRange =
      termDigits &&
      !isNaN(termNum) &&
      termNum >= APPLICATION_PRODUCT.termMin &&
      termNum <= APPLICATION_PRODUCT.termMax;
    const resolvedTerm = termInRange ? termDigits : '';
    return {
      firstName: '',
      lastName: '',
      phone: '',
      address: '',
      loanAmount: withinRange ? digits : '',
      loanTerm: resolvedTerm,
    };
  }, [prefillAmount, prefillTerm]);

  const [formData, setFormData] = useState(initial);
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedPhone, setSubmittedPhone] = useState('');

  const fieldRefs = useRef({});
  const setFieldRef = (name) => (el) => { fieldRefs.current[name] = el; };

  // Reset form when prefill amount changes (modal reopened with a new amount)
  useEffect(() => {
    setFormData(initial);
    setPrivacyConsent(false);
    setErrors({});
    setSubmitted(false);
    setSubmittedPhone('');
  }, [initial]);

  const validateForm = () => {
    const e = {};
    if (!formData.firstName || formData.firstName.trim().length < 2) e.firstName = 'Adınızı daxil edin';
    if (!formData.lastName || formData.lastName.trim().length < 2) e.lastName = 'Soyadınızı daxil edin';
    if (!formData.phone) e.phone = 'Telefon nömrəsini daxil edin';
    else if (!validatePhone(formData.phone)) e.phone = 'Format: +994 XX XXX XX XX';
    if (!formData.address || formData.address.trim().length < 5) e.address = 'Faktiki yaşadığınız ünvanı daxil edin';

    const amt = Number(formData.loanAmount);
    if (!formData.loanAmount || isNaN(amt)) {
      e.loanAmount = 'Məbləği daxil edin';
    } else if (amt < AMOUNT_MIN_APP || amt > AMOUNT_MAX_APP) {
      e.loanAmount = `Məbləğ ${formatAmountAz(AMOUNT_MIN_APP)} – ${formatAmountAz(AMOUNT_MAX_APP)} AZN arası olmalıdır`;
    }

    const term = Number(formData.loanTerm);
    if (!formData.loanTerm || isNaN(term)) {
      e.loanTerm = 'Müddəti seçin';
    } else if (term < APPLICATION_PRODUCT.termMin || term > APPLICATION_PRODUCT.termMax) {
      e.loanTerm = `Müddət ${APPLICATION_PRODUCT.termMin} – ${APPLICATION_PRODUCT.termMax} ay arası olmalıdır`;
    }

    if (!privacyConsent) e.privacyConsent = 'Məxfilik razılığı tələb olunur';
    setErrors(e);
    return e;
  };

  const focusFirstInvalid = (errObj) => {
    const first = FIELD_ORDER.find((f) => errObj[f]);
    if (!first) return;
    const el = fieldRefs.current[first];
    if (el && typeof el.focus === 'function') {
      try { el.focus({ preventScroll: false }); } catch { el.focus(); }
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: '' });
  };

  const handleAmountChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '');
    setFormData({ ...formData, loanAmount: digits });
    if (errors.loanAmount) setErrors({ ...errors, loanAmount: '' });
  };

  const handleTermChange = (value) => {
    setFormData({ ...formData, loanTerm: value });
    if (errors.loanTerm) setErrors({ ...errors, loanTerm: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    const errObj = validateForm();
    if (Object.keys(errObj).length > 0) {
      focusFirstInvalid(errObj);
      toast({
        title: 'Formanı yoxlayın',
        description: 'Bəzi məcburi sahələr düzgün doldurulmayıb',
        variant: 'destructive',
      });
      return;
    }
    setIsSubmitting(true);
    try {
      await applicationsApi.create({
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        phone: formData.phone.trim(),
        address: formData.address.trim(),
        productType: APPLICATION_PRODUCT_LABEL,
        loanAmount: Number(formData.loanAmount),
        loanTerm: Number(formData.loanTerm),
        privacyConsent: true,
        applicationType: 'preliminary_credit',
      });
      setSubmittedPhone(formData.phone);
      setSubmitted(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      let msg = 'Müraciət göndərilə bilmədi. Xahiş edirik yenidən cəhd edin.';
      if (typeof detail === 'string') msg = detail;
      else if (Array.isArray(detail)) msg = detail.map((d) => d.msg || '').filter(Boolean).join('; ') || msg;
      toast({ title: 'Xəta baş verdi', description: msg, variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className={isModal ? 'p-6 sm:p-8 text-center' : 'min-h-screen bg-gray-50 flex items-center justify-center px-4 py-16'}>
        <div
          className={
            isModal
              ? ''
              : 'max-w-lg w-full bg-white rounded-2xl border border-slate-200/80 shadow-sm p-8 sm:p-10 text-center'
          }
          data-testid="application-success"
        >
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100 mx-auto mb-5">
            <CheckCircle2 className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">Təşəkkür edirik!</h2>
          <p className="text-base text-gray-700 mb-5 leading-relaxed">
            Müraciətiniz qəbul edildi. Əməkdaşımız <strong>1 iş günü ərzində</strong> sizinlə əlaqə saxlayacaq.
          </p>
          {submittedPhone && (
            <p className="text-sm text-gray-500 mb-6">
              Əlaqə nömrəsi: <strong className="text-gray-900">{submittedPhone}</strong>
            </p>
          )}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {isModal ? (
              <Button
                onClick={onSuccess}
                className="bg-blue-700 hover:bg-blue-800 text-white"
                data-testid="app-success-close-btn"
              >
                Bağla
              </Button>
            ) : (
              <>
                <Button asChild variant="outline" data-testid="back-home-btn">
                  <Link to="/">Ana səhifə</Link>
                </Button>
                <Button
                  onClick={onSuccess}
                  className="bg-blue-700 hover:bg-blue-800 text-white"
                  data-testid="new-app-btn"
                >
                  Yeni müraciət göndər
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      className={
        isModal
          ? 'px-6 sm:px-7 pt-6 pb-6 sm:pb-7'
          : 'bg-white border border-slate-200/80 rounded-2xl shadow-[0_1px_2px_rgba(15,23,42,0.04),0_4px_12px_rgba(15,23,42,0.03)] p-5 sm:p-7 lg:p-8'
      }
      data-testid="application-form"
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field id="firstName" label="Ad *" error={errors.firstName}>
            <Input
              id="firstName"
              name="firstName"
              ref={setFieldRef('firstName')}
              value={formData.firstName}
              onChange={handleChange}
              placeholder="Adınız"
              data-testid="app-firstname-input"
              className={`h-11 ${errors.firstName ? 'border-red-500' : ''}`}
              autoComplete="given-name"
              aria-invalid={!!errors.firstName}
              aria-describedby={errors.firstName ? 'firstName-error' : undefined}
            />
          </Field>
          <Field id="lastName" label="Soyad *" error={errors.lastName}>
            <Input
              id="lastName"
              name="lastName"
              ref={setFieldRef('lastName')}
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Soyadınız"
              data-testid="app-lastname-input"
              className={`h-11 ${errors.lastName ? 'border-red-500' : ''}`}
              autoComplete="family-name"
              aria-invalid={!!errors.lastName}
              aria-describedby={errors.lastName ? 'lastName-error' : undefined}
            />
          </Field>
        </div>

        <Field
          id="phone"
          label="Telefon nömrəsi *"
          error={errors.phone}
          hint="Bu nömrə üzərindən sizinlə əlaqə saxlanacaq."
        >
          <Input
            id="phone"
            name="phone"
            type="tel"
            ref={setFieldRef('phone')}
            value={formData.phone}
            onChange={(e) => {
              const masked = formatAzPhone(e.target.value);
              setFormData({ ...formData, phone: masked });
              if (errors.phone) setErrors({ ...errors, phone: '' });
            }}
            placeholder="+994 XX XXX XX XX"
            data-testid="app-phone-input"
            className={`h-11 ${errors.phone ? 'border-red-500' : ''}`}
            autoComplete="tel"
            maxLength={17}
            inputMode="tel"
            aria-invalid={!!errors.phone}
            aria-describedby={errors.phone ? 'phone-error' : 'phone-hint'}
          />
        </Field>

        <Field id="address" label="Faktiki yaşadığınız ünvan *" error={errors.address}>
          <Input
            id="address"
            name="address"
            ref={setFieldRef('address')}
            value={formData.address}
            onChange={handleChange}
            placeholder="Şəhər, rayon, küçə, ev №"
            data-testid="app-address-input"
            className={`h-11 ${errors.address ? 'border-red-500' : ''}`}
            autoComplete="street-address"
            aria-invalid={!!errors.address}
            aria-describedby={errors.address ? 'address-error' : undefined}
            maxLength={200}
          />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            id="loanAmount"
            label="Məbləğ (AZN) *"
            error={errors.loanAmount}
            hint={`${formatAmountAz(AMOUNT_MIN_APP)} – ${formatAmountAz(AMOUNT_MAX_APP)} AZN`}
          >
            <Input
              id="loanAmount"
              name="loanAmount"
              type="text"
              inputMode="numeric"
              ref={setFieldRef('loanAmount')}
              value={formatAmountAz(formData.loanAmount)}
              onChange={handleAmountChange}
              placeholder={`Məs.: ${formatAmountAz(AMOUNT_MIN_APP)}`}
              data-testid="app-amount-input"
              aria-invalid={!!errors.loanAmount}
              aria-describedby={
                errors.loanAmount ? 'loanAmount-error' : 'loanAmount-hint'
              }
              className={`h-11 ${errors.loanAmount ? 'border-red-500' : ''}`}
            />
          </Field>

          <Field
            id="loanTerm"
            label="Müddət (ay) *"
            error={errors.loanTerm}
            hint={`${APPLICATION_PRODUCT.termMin} – ${APPLICATION_PRODUCT.termMax} ay arası`}
          >
            <Input
              id="loanTerm"
              name="loanTerm"
              type="text"
              inputMode="numeric"
              ref={setFieldRef('loanTerm')}
              value={formData.loanTerm}
              onChange={(e) => handleTermChange(e.target.value.replace(/\D/g, ''))}
              placeholder={`Məs.: ${APPLICATION_PRODUCT.termMin}`}
              data-testid="app-term-input"
              aria-invalid={!!errors.loanTerm}
              aria-describedby={errors.loanTerm ? 'loanTerm-error' : 'loanTerm-hint'}
              className={`h-11 ${errors.loanTerm ? 'border-red-500' : ''}`}
            />
          </Field>
        </div>
      </div>

      {/* Privacy consent */}
      <div className="mt-6">
        <label className="flex items-start gap-3 cursor-pointer">          <Checkbox
            checked={privacyConsent}
            ref={setFieldRef('privacyConsent')}
            onCheckedChange={(v) => {
              setPrivacyConsent(!!v);
              if (errors.privacyConsent) setErrors({ ...errors, privacyConsent: '' });
            }}
            className="mt-0.5"
            data-testid="app-consent-checkbox"
            aria-invalid={!!errors.privacyConsent}
            aria-describedby={errors.privacyConsent ? 'privacyConsent-error' : undefined}
          />
          <span className="text-sm text-gray-700 leading-relaxed">
            <Link to="/mexfilik-siyaseti" className="text-blue-700 hover:underline" target="_blank" rel="noopener noreferrer">
              Məxfilik siyasəti
            </Link>{' '}
            və{' '}
            <Link to="/istifade-shertleri" className="text-blue-700 hover:underline" target="_blank" rel="noopener noreferrer">
              İstifadə şərtləri
            </Link>{' '}
            ilə tanış oldum və şəxsi məlumatlarımın emalına razılıq verirəm.
          </span>
        </label>
        {errors.privacyConsent && (
          <p
            id="privacyConsent-error"
            role="alert"
            className="text-xs text-red-500 ml-7 mt-2"
          >
            {errors.privacyConsent}
          </p>
        )}
      </div>

      {/* Notice */}
      <div
        className="mt-6 rounded-lg border border-blue-100 bg-blue-50/60 px-3 py-2"
        data-testid="app-notice"
      >
        <p className="text-[11px] sm:text-xs text-gray-600 leading-snug">
          Bu forma yalnız ilkin müraciət üçündür və avtomatik kredit təsdiqi vermir.
          Yekun məbləğ və şərtlər müraciətin qiymətləndirilməsindən sonra müəyyən edilir.
        </p>
      </div>

      {/* Submit */}
      <div className="mt-6">
        <Button
          type="submit"
          className="w-full bg-blue-700 hover:bg-blue-800 text-white h-11 text-base font-semibold"
          disabled={isSubmitting}
          data-testid="app-submit-btn"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" aria-hidden="true" />
              Göndərilir...
            </>
          ) : (
            <>
              <Send className="mr-2 h-4 w-4 opacity-80" aria-hidden="true" />
              Müraciəti göndər
            </>
          )}
        </Button>
      </div>
    </form>
  );
};

const Field = ({ id, label, error, hint, children }) => (
  <div>
    <Label htmlFor={id} className="mb-2 block text-sm text-gray-800">
      {label}
    </Label>
    {children}
    {error ? (
      <p id={`${id}-error`} role="alert" className="text-xs text-red-500 mt-1.5">
        {error}
      </p>
    ) : hint ? (
      <p id={`${id}-hint`} className="text-xs text-gray-500 mt-1.5">
        {hint}
      </p>
    ) : null}
  </div>
);
